import { AgTechIndex } from "@agtech/core/utilities/AgTechUtilities"
import { SaleBidder, SaleBidderByNumberIndex } from "../bidders/BidderData"
import { SaleLotCategoryIndex } from "./categories/LotCategoryData"
import { AgTechContact } from "@agtech/data/contacts/ContactData"
import { useSelector } from 'react-redux';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { SalesAppState } from "../SalesAppDataStore"

export type LotIndex = {
    [id: string]: SaleLot
}

export type SaleLot = {
    id: number,
    saleId: number,
    saleOrder: number,
    lotNumber: string,
    lotDescription: string,
    purchasePrice?: number,
    bidderNumber?: string,
    lotCategoryId?: number,
    timestamp: string,
    invoiceState: LotInvoiceState,
    isSplit: boolean,
    groupSize?: number,
    originalBidderId?: number,
    originalBidder?: SaleBidder
}

export type SaleLotData = {
    lots: AgTechIndex<SaleLot>,
    lotCategories: SaleLotCategoryIndex,
    bidders: SaleBidderByNumberIndex,
    contacts: AgTechIndex<AgTechContact>
}

export enum LotInvoiceState {
    NotInvoiced = 0,
    PartiallyInvoiced = 1,
    Invoiced = 2
}

export const DefaultSaleLot: SaleLot = {
    id: 0,
    saleId: 0,
    saleOrder: 0,
    lotNumber: '',
    lotDescription: '',
    timestamp: '',
    invoiceState: LotInvoiceState.NotInvoiced,
    isSplit: false
}

export type AgTechImportedSaleLot = SaleLot & {
    rowId: number
}

export type AgTechImportedSaleLotIndex = {
    [rowId: number]: AgTechImportedSaleLot
}

export type LotBidderRefreshData = {
    existingBidderNumber: string,
    updatedBidderNumber: string,
    updatedBidderId: number
}

export type SaleLotComparison = {
    saleOrder: number,
    lotNumber: string,
    lotDescription: string,
    bidderNumber?: string,
    purchasePrice?: number,
    lotCategoryId?: number,
    timestamp: string,
    isInvoiced: boolean,
}

export type LotSplit = {
    contactId: number,
    bidderId?: number,
    percentage: number,
}

export type LotSplitSummary = {
    lot: SaleLot,
    splits: LotSplit[]
}

export type ReorderLotSaleOrderDTO = {
    lotId: number,
    newSaleOrder: number
};

export type SaleLotOrderModificationGridEntity = {
    id: number,
    saleOrder: number,
    originalOrder: number,
    lotNumber: string,
    lotDescription: string,
    changed: boolean
};

export const isLotTheSame = (prev: SaleLot, curr: SaleLot) => {
    return prev.saleOrder === curr.saleOrder &&
           prev.lotNumber === curr.lotNumber &&
           prev.lotDescription === curr.lotDescription &&
           prev.originalBidderId === curr.originalBidderId &&
           prev.purchasePrice === curr.purchasePrice &&
           prev.lotCategoryId === curr.lotCategoryId &&
           prev.timestamp === curr.timestamp &&
           prev.invoiceState === curr.invoiceState &&
           prev.isSplit === curr.isSplit;
}

export const isLotSold = (lot: SaleLot) => lot.originalBidderId && lot.purchasePrice;

export const isLotInvoiced = (lot: SaleLot) => lot.invoiceState === LotInvoiceState.Invoiced;

export const getLotStatus = (lot: SaleLot) => {
    return isLotInvoiced(lot) ? 'Invoiced'
        : lot.purchasePrice ? 'Sold'
        : 'Not sold';
}

/// Hooks

export type SaleLotGridEntity = SaleLot & {
    id: number,
    saleOrder: number,
    lotNumber: string,
    purchasePrice?: number,
    bidderNumber?: string,
    isInvoiced: boolean,
    lotCategoryId?: number,
    lotCategoryName?: string,
    lotCategoryColor?: string,
    lotContact?: AgTechContact
}

export const useLotGridEntity = (lotData: SaleLotData, lotFilter?: (lot: SaleLot) => boolean): SaleLotGridEntity[] => {
    const lots = Object.values(lotData.lots);
    const lotsToShow = lotFilter ? lots.filter(lotFilter) : lots;

    return lotsToShow.map(lot => {
        const lotCategory = lot.lotCategoryId ? lotData.lotCategories[lot.lotCategoryId] : undefined;
        const lotBidder = lot.originalBidderId ? lotData.bidders[lot.originalBidderId] : undefined;
        
        return {
            ...lot,
            bidderNumber: lotBidder?.bidderNumber,
            purchasePrice: lot.purchasePrice,
            lotContact: lotBidder?.contact,
            lotCategoryId: lotCategory?.id,
            lotCategoryName: lotCategory?.name,
            lotCategoryColor: lotCategory?.color,
            isInvoiced: isLotInvoiced(lot)
        }
    });
}

/// Store

export type LotData = {
    trackedLots: SaleLot[],
    removedLots: number[]
}

const DefaultLotData: LotData = {
    trackedLots: [],
    removedLots: []
}

export const lotDataSlice = createSlice({
    name: 'lots',
    initialState: DefaultLotData,
    reducers: {
        updateLot: (state, action: PayloadAction<SaleLot>) => {
            const updatedState = {
                ...state,
                trackedLots: [...state.trackedLots, action.payload]
            };

            return updatedState;
        },            
        removeLot: (state, action: PayloadAction<number>) => {
            return {
                ...state,
                removedLots: [...state.removedLots, action.payload]
            }
        },
        resetLots: () => {
            return DefaultLotData;
        }
    }
});

export const useLotTrackingData = (): LotData => useSelector((state: SalesAppState) => state.lots);


export default lotDataSlice.reducer;